import React, { useContext, useEffect } from 'react';
import type { ComponentData } from 'ricos-content';
import { RicosContext } from 'ricos-context';
import type { LegacyEditorPluginConfig } from 'ricos-types';
import { containsUrl, normalizeUrl } from 'wix-rich-content-common';

import htmlStyles from '../../statics/styles/HtmlComponent.scss';
import { ROOT } from '../dataHooks';
import { INIT_HEIGHT, INIT_WIDTH, SRC_TYPE_HTML, SRC_TYPE_URL } from '../defaults';
import { htmlFixer } from '../htmlFixer';
import IframeHtml from '../IframeHtml';
import IframeUrl from '../IframeUrl';

const getPageURL = siteDomain => {
  if (!siteDomain) {
    return;
  }

  const regex = /http.+com/gm;
  const res = regex.exec(siteDomain);
  if (res) {
    return res[0];
  }
};

export const HtmlViewer: React.FC<{
  settings: LegacyEditorPluginConfig;
  componentData: ComponentData;
  iframeSandboxDomain?: string;
  isViewer?: boolean;
}> = ({ settings, componentData, iframeSandboxDomain, isViewer = false }) => {
  const { isMobile } = useContext(RicosContext);
  const [elementRef, setElementRef] = React.useState<HTMLElement | null>(null);
  const [iframeHeight, setIframeHeight] = React.useState<number | undefined>();

  useEffect(() => {
    const { width, height } = settings;
    const { containerData } = componentData;
    if (!containerData.width) {
      if (width) {
        containerData.width = { custom: width };
      } else if (elementRef) {
        containerData.width = { custom: elementRef.getBoundingClientRect().width };
      } else {
        containerData.width = { custom: INIT_WIDTH };
      }
    }

    if (!containerData.height) {
      containerData.height = { custom: height || INIT_HEIGHT };
    }
  }, [elementRef]);

  const setHeight = height => {
    if (height > 0 && height !== iframeHeight) {
      setIframeHeight(height);
    }
  };

  const getIframeHeight = () => {
    const { containerData } = componentData;
    //for avoiding unnecessary scroll
    const maxDiff = iframeHeight && Math.min(50, iframeHeight * 0.1);
    let normalizedHeight = iframeHeight;

    const customHeight = containerData.height?.custom;

    if (customHeight) {
      if (typeof customHeight === 'string' && customHeight.endsWith('%')) {
        return normalizedHeight || settings.height || INIT_HEIGHT;
      }

      if (!isNaN(parseInt(customHeight))) {
        normalizedHeight =
          iframeHeight && customHeight < iframeHeight && customHeight + maxDiff > iframeHeight
            ? iframeHeight
            : customHeight;
      }
    }

    return normalizedHeight || settings.height || INIT_HEIGHT;
  };

  const style = {
    width: isMobile
      ? 'auto'
      : componentData.containerData.width?.custom || settings.width || INIT_WIDTH + 'px',
    height: getIframeHeight() + 'px',
    maxHeight: iframeHeight,
  };

  let html;
  if (componentData.html) {
    const pageURL = getPageURL(settings.siteDomain);
    html = htmlFixer(componentData.html, pageURL);
  }

  return (
    <div className={htmlStyles.htmlComponent} ref={setElementRef} style={style} data-hook={ROOT}>
      {html ? (
        <IframeHtml
          iframeSandboxDomain={iframeSandboxDomain || 'https://www.filesusr.com'}
          key={SRC_TYPE_HTML}
          tabIndex={0}
          html={html}
          onHeightChange={setHeight}
          isViewer={isViewer}
        />
      ) : componentData.url && containsUrl(componentData.url) ? (
        <IframeUrl
          key={SRC_TYPE_URL}
          // @ts-ignore
          tabIndex={0}
          src={normalizeUrl(componentData.url)}
          isViewer={isViewer}
        />
      ) : (
        <div className={htmlStyles.htmlComponent_placeholder} />
      )}
    </div>
  );
};
